import { gql } from '@apollo/client';

// ── Queries ───────────────────────────────────────────────────────────────────

export const GET_SUBSCRIPTION_PLANS = gql`
  query GetSubscriptionPlans {
    subscriptionPlans {
      id
      planId
      name
      description
      price
      currency
      durationDays
      grantedRoleId
      subscriptionScope
      isActive
      usersIncluded
      costPerUser
      taxRatePercent
      processingFeeAmount
    }
  }
`;

export const GET_MY_SUBSCRIPTION = gql`
  query GetMySubscription {
    mySubscription {
      id
      userId
      planId
      status
      startDate
      expiryDate
      createdDate
      updatedDate
    }
  }
`;

export const GET_TENANT_SUBSCRIPTION = gql`
  query GetTenantSubscription($tenantId: String!) {
    tenantSubscription(tenantId: $tenantId) {
      id
      tenantId
      planId
      status
      startDate
      expiryDate
      paidByUserId
      monthlyElevenLabsLimitPerUser
      monthlyOpenAIImageLimitPerUser
      aiUsageYearMonth
      elevenLabsUsedThisMonth
      openAIImageUsedThisMonth
      createdDate
      updatedDate
    }
  }
`;

// ── Mutations ─────────────────────────────────────────────────────────────────

export const CREATE_PERSONAL_CHECKOUT = gql`
  mutation CreatePersonalCheckout(
    $planId: String!
    $successUrl: String!
    $cancelUrl: String!
  ) {
    createPersonalCheckout(
      planId: $planId
      successUrl: $successUrl
      cancelUrl: $cancelUrl
    ) {
      checkoutUrl
    }
  }
`;

export const CREATE_TENANT_CHECKOUT = gql`
  mutation CreateTenantCheckout(
    $tenantId: String!
    $planId: String!
    $successUrl: String!
    $cancelUrl: String!
  ) {
    createTenantCheckout(
      tenantId: $tenantId
      planId: $planId
      successUrl: $successUrl
      cancelUrl: $cancelUrl
    ) {
      checkoutUrl
    }
  }
`;

export const CANCEL_MY_SUBSCRIPTION = gql`
  mutation CancelMySubscription {
    cancelMySubscription
  }
`;

export const VERIFY_PERSONAL_CHECKOUT = gql`
  mutation VerifyPersonalCheckout($transactionId: String!) {
    verifyPersonalCheckout(transactionId: $transactionId) {
      id
      userId
      planId
      status
      startDate
      expiryDate
      createdDate
      updatedDate
    }
  }
`;

export const CANCEL_TENANT_SUBSCRIPTION = gql`
  mutation CancelTenantSubscription($tenantId: String!) {
    cancelTenantSubscription(tenantId: $tenantId)
  }
`;

export const VERIFY_TENANT_CHECKOUT = gql`
  mutation VerifyTenantCheckout($tenantId: String!, $transactionId: String!) {
    verifyTenantCheckout(tenantId: $tenantId, transactionId: $transactionId) {
      id
      tenantId
      planId
      status
      startDate
      expiryDate
      paidByUserId
      createdDate
      updatedDate
    }
  }
`;

export const GRANT_TENANT_SUBSCRIPTION = gql`
  mutation GrantTenantSubscription(
    $tenantId: String!
    $planId: String!
    $startDate: DateTime!
    $expiryDate: DateTime!
    $referenceNote: String
    $usersIncluded: Int
    $costPerUser: Float
    $taxRatePercent: Float
    $processingFeeAmount: Float
  ) {
    grantTenantSubscription(
      tenantId: $tenantId
      planId: $planId
      startDate: $startDate
      expiryDate: $expiryDate
      referenceNote: $referenceNote
      usersIncluded: $usersIncluded
      costPerUser: $costPerUser
      taxRatePercent: $taxRatePercent
      processingFeeAmount: $processingFeeAmount
    ) {
      id
      tenantId
      planId
      status
      startDate
      expiryDate
      usersIncluded
      costPerUser
      taxRatePercent
      processingFeeAmount
      createdDate
      updatedDate
    }
  }
`;

export const GET_TENANT_INVOICES = gql`
  query GetTenantInvoices($tenantId: String!) {
    tenantInvoices(tenantId: $tenantId) {
      id
      tenantId
      tenantSubscriptionId
      invoiceNumber
      amount
      amountPaid
      issuedDate
      dueDate
      status
      notes
      payments {
        id
        paymentDate
        amount
        method
        referenceNumber
        notes
        loggedDate
      }
      createdDate
      updatedDate
    }
  }
`;

export const LOG_TENANT_INVOICE = gql`
  mutation LogTenantInvoice(
    $tenantId: String!
    $subscriptionId: String
    $invoiceNumber: String!
    $amount: Float!
    $issuedDate: DateTime!
    $dueDate: DateTime!
    $notes: String
  ) {
    logTenantInvoice(
      tenantId: $tenantId
      subscriptionId: $subscriptionId
      invoiceNumber: $invoiceNumber
      amount: $amount
      issuedDate: $issuedDate
      dueDate: $dueDate
      notes: $notes
    ) {
      id
      invoiceNumber
      amount
      amountPaid
      issuedDate
      dueDate
      status
      notes
      payments {
        id
        paymentDate
        amount
        method
        referenceNumber
        loggedDate
      }
    }
  }
`;

export const LOG_TENANT_INVOICE_PAYMENT = gql`
  mutation LogTenantInvoicePayment(
    $invoiceId: String!
    $paymentDate: DateTime!
    $amount: Float!
    $method: String!
    $referenceNumber: String
    $notes: String
  ) {
    logTenantInvoicePayment(
      invoiceId: $invoiceId
      paymentDate: $paymentDate
      amount: $amount
      method: $method
      referenceNumber: $referenceNumber
      notes: $notes
    ) {
      id
      invoiceNumber
      amount
      amountPaid
      status
      payments {
        id
        paymentDate
        amount
        method
        referenceNumber
        loggedDate
      }
    }
  }
`;

export const DELETE_TENANT_INVOICE = gql`
  mutation DeleteTenantInvoice($invoiceId: String!) {
    deleteTenantInvoice(invoiceId: $invoiceId)
  }
`;

export const DELETE_TENANT_INVOICE_PAYMENT = gql`
  mutation DeleteTenantInvoicePayment($invoiceId: String!, $paymentId: String!) {
    deleteTenantInvoicePayment(invoiceId: $invoiceId, paymentId: $paymentId) {
      id
      invoiceNumber
      amount
      amountPaid
      status
      payments {
        id
        paymentDate
        amount
        method
        referenceNumber
        loggedDate
      }
    }
  }
`;
